<div class="import-linesheet">
  <!-- Header + progress spine -->
  <header class="import-header">
    <h3 class="import-title">{{ 'UPLOAD_EXCEL_ORDERSHEET' | translate | uppercase }}</h3>

    <nav class="import-steps" aria-label="Progress">
      <span class="import-step" [class.active]="currentStep === 1" [class.done]="currentStep > 1">
        <span class="step-index"><i class="fa-solid fa-check"></i><span class="step-number">1</span></span>
        <span class="step-label">{{ 'FORMAT' | translate }}</span>
      </span>
      <span class="step-divider" aria-hidden="true"></span>
      <span class="import-step" [class.active]="currentStep === 2" [class.done]="currentStep > 2">
        <span class="step-index"><i class="fa-solid fa-check"></i><span class="step-number">2</span></span>
        <span class="step-label">{{ 'FILE' | translate }}</span>
      </span>
      <span class="step-divider" aria-hidden="true"></span>
      <span class="import-step" [class.active]="currentStep === 3">
        <span class="step-index"><span class="step-number">3</span></span>
        <span class="step-label">{{ 'SUMMARY' | translate }}</span>
      </span>
    </nav>
  </header>

  <!-- STEP 1: choose a format -->
  <ng-container *ngIf="currentStep === 1">
    <div class="format-grid">
      <div class="format-card" role="button" tabindex="0"
        *ngIf="isFormatAvailable(MATRIX_FORMAT)"
        (click)="selectFormat(MATRIX_FORMAT)" (keydown.enter)="selectFormat(MATRIX_FORMAT)">
        <i class="format-icon fa-regular fa-table-cells"></i>
        <h5 class="format-name" translate>MATRIX_FORMAT</h5>
        <p class="format-desc" translate>UPLOAD_ORDER_EXCEL_MATRIX_INFO</p>
        <span class="format-template format-template--muted"
          ngbTooltip="{{ 'EXAMPLE_FILE_CANNOT_BE_PROVIDED_DOWNLOAD_LINESHEET_FROM_PRODUCT_EXPORTS' | translate }}">
          <i class="fa-regular fa-circle-info mr-1"></i>{{ 'DOWNLOAD_EXAMPLE_TEMPLATE' | translate }}
        </span>
      </div>

      <div class="format-card" role="button" tabindex="0"
        *ngIf="isFormatAvailable(COLUMN_FORMAT)"
        (click)="selectFormat(COLUMN_FORMAT)" (keydown.enter)="selectFormat(COLUMN_FORMAT)">
        <i class="format-icon fa-regular fa-columns-3"></i>
        <h5 class="format-name" translate>COLUMN_FORMAT</h5>
        <p class="format-desc" translate>UPLOAD_ORDER_EXCEL_COLUMN_INFO</p>
        <button type="button" class="format-template"
          (click)="$event.stopPropagation(); downloadOrderExampleFile()">
          <i class="fa-regular fa-file-arrow-down mr-1"></i>{{ 'DOWNLOAD_EXAMPLE_TEMPLATE' | translate }}
        </button>
      </div>

      <div class="format-card" role="button" tabindex="0"
        *ngIf="isFormatAvailable(MULTI_ORDER_COLUMN_FORMAT) && isSalesOrAdmin && (featureActivatedGuardWrapper.isActivated('gung', '3.0.20', true) | async)"
        (click)="selectFormat(MULTI_ORDER_COLUMN_FORMAT)" (keydown.enter)="selectFormat(MULTI_ORDER_COLUMN_FORMAT)">
        <i class="format-icon fa-regular fa-boxes-stacked"></i>
        <h5 class="format-name" translate>MULTI_ORDER_COLUMN_FORMAT</h5>
        <p class="format-desc" translate>UPLOAD_ORDER_EXCEL_MULTI_ORDER_COLUMN_FORMAT_INFO</p>
        <button type="button" class="format-template"
          (click)="$event.stopPropagation(); downloadMultiOrderExampleFile()">
          <i class="fa-regular fa-file-arrow-down mr-1"></i>{{ 'DOWNLOAD_EXAMPLE_TEMPLATE' | translate }}
        </button>
      </div>
    </div>
  </ng-container>

  <!-- STEP 2: add the file -->
  <ng-container *ngIf="currentStep === 2">
    <div class="selected-format-bar" *ngIf="selectedFormatMeta">
      <i class="selected-format-icon fa-regular" [ngClass]="selectedFormatMeta.icon"></i>
      <span class="selected-format-name">{{ selectedFormatMeta.titleKey | translate }}</span>
      <button type="button" class="btn btn-link btn-sm selected-format-change"
        (click)="cancelUpload()" [disabled]="loading ? true : null">
        {{ 'CHANGE' | translate }}
      </button>
    </div>

    <div *ngIf="uploadFormat === MATRIX_FORMAT" class="mode-field">
      <label class="d-block mb-1" translate>MODE</label>
      <lib-gung-select-option-input [suppressLabel]="true" [optionsList]="replaceModeOptions"
        [(ngModel)]="replaceMode" [disabled]="loading ? true : null"></lib-gung-select-option-input>
    </div>

    <input #inputFile type="file" class="d-none" id="file-upload" accept=".xlsx,.xls"
      [multiple]="false" [disabled]="loading ? true : null" (change)="onFileInputChange($event)" />

    <div class="file-drop-zone" [class.drag-over]="isDragOver" [class.has-file]="!!selectedFile"
      [class.disabled]="loading" role="button" tabindex="0"
      (click)="!selectedFile && !loading && inputFile.click()"
      (keydown.enter)="!selectedFile && !loading && inputFile.click()"
      (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">

      <ng-container *ngIf="!selectedFile">
        <i class="fa-regular fa-cloud-arrow-up drop-icon"></i>
        <p class="drop-title mb-1">{{ 'DROP_FILE_TO_UPLOAD' | translate }}</p>
        <p class="drop-subtitle mb-2">{{ 'OR_CLICK_TO_BROWSE' | translate }}</p>
        <button type="button" class="btn btn-outline-secondary btn-sm"
          (click)="$event.stopPropagation(); inputFile.click()" [disabled]="loading ? true : null">
          <i class="fa-regular fa-folder-open mr-1"></i>{{ 'BROWSE_FILES' | translate }}
        </button>
        <p class="drop-hint mb-0">.xlsx, .xls</p>
      </ng-container>

      <div *ngIf="selectedFile" class="selected-file">
        <i class="fa-regular fa-file-excel file-icon"></i>
        <span class="file-name">{{ selectedFile.name }}</span>
        <button type="button" class="btn btn-link btn-remove-file p-0"
          (click)="$event.stopPropagation(); clearSelectedFile(inputFile)"
          [disabled]="loading ? true : null" [attr.aria-label]="'REMOVE' | translate">
          <i class="fa-regular fa-xmark"></i>
        </button>
      </div>
    </div>

    <div class="import-actions">
      <button type="button" class="btn btn-link btn-sm" (click)="cancelUpload(inputFile)"
        [disabled]="loading ? true : null" translate>
        CANCEL
      </button>
      <button type="button" class="btn btn-primary btn-sm px-5 ml-2" (click)="submitUpload()"
        [disabled]="!selectedFile || loading ? true : null" translate>
        UPLOAD <i *ngIf="loading" class="fa fa-spinner fa-spin fa-lg"></i>
      </button>
    </div>
  </ng-container>

  <!-- STEP 3: review the parsed result -->
  <ng-container *ngIf="result && (uploadFormat === MATRIX_FORMAT || uploadFormat === COLUMN_FORMAT)">
    <div class="selected-format-bar" *ngIf="selectedFormatMeta">
      <i class="selected-format-icon fa-regular" [ngClass]="selectedFormatMeta.icon"></i>
      <span class="selected-format-name">{{ selectedFormatMeta.titleKey | translate }}</span>
    </div>

    <div class="verdict-bar">
      <span class="verdict-item">
        <strong>{{ result.rows?.length || 0 }}</strong> {{ 'ROWS' | translate }}
      </span>
      <span class="verdict-item verdict-item--error" *ngIf="result.errors?.length">
        <i class="fa-solid fa-circle-exclamation mr-1"></i>
        <strong>{{ result.errors.length }}</strong> {{ 'ERRORS' | translate }}
      </span>
    </div>

    <div *ngIf="result.errors && result.errors.length > 0" class="row mt-3">
      <div class="col-12">
        <table class="table">
          <thead>
            <tr>
              <th scope="col" translate>ERRORS</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let error of result.errors">
              <td>{{ error }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <table class="table table-striped">
          <thead class="table-dark">
            <tr>
              <th scope="col">#</th>
              <th scope="col" translate>ITEM_NO</th>
              <th scope="col" translate>EAN</th>
              <th scope="col" translate>QUANTITY</th>
              <th scope="col" translate>PRODUCT</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of result.rows">
              <th scope="row">{{ row.rownr }}</th>
              <td>
                @if(row.customerSpecific?.customerProductId){
                {{row.customerSpecific.customerProductId }}
                }@else{
                {{ row.product.id.includes('_GUNGDOT_') ? replaceGungDot(row.product.id) : row.product.id }}
                }

              </td>
              <td>{{ row.eanCode }}</td>
              <td>{{ row.quantity }}</td>
              <td>
                <a [routerLink]="['/product/', row.productId]" target="_blank">
                  @if(row.customerSpecific?.customerProductId){
                  {{row.customerSpecific.customerProductId }}
                  }@else{
                  {{ row.product.id.includes('_GUNGDOT_') ? replaceGungDot(row.product.id) : row.product.id }}
                  }
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div *ngIf="quantityChanged" class="alert alert-warning" role="alert" translate>
      QUANTITY_CHANGED_IMPORT_WARNING
    </div>

    <div class="import-actions import-actions--review" *ngIf="!loading">
      <button type="button" class="btn btn-outline-danger btn-sm" translate
        (click)="uploadFormat = undefined; result = undefined">
        CANCEL
      </button>
      <div class="review-commit ml-auto">
        <span *ngIf="
            uploadFormat === 'COLUMN_FORMAT' ||
            (uploadFormat === 'MATRIX_FORMAT' && replaceMode === 'REPLACE_CURRENT_ORDER')
          " class="replace-note" translate>REPLACE_CART_INFO</span>
        <button type="button" class="btn btn-primary btn-sm px-4" translate (click)="loadProductsToCart()">
          {{
          uploadFormat === 'COLUMN_FORMAT' ||
          (uploadFormat === 'MATRIX_FORMAT' && replaceMode === 'REPLACE_CURRENT_ORDER')
          ? 'REPLACE_CART'
          : 'ADD_TO_ORDER'
          }}
        </button>
      </div>
    </div>
  </ng-container>

  <ng-container *ngIf="multiOrderResult && (uploadFormat === MULTI_ORDER_COLUMN_FORMAT)">
    <lib-multi-order-excel-import-result [result]="multiOrderResult"></lib-multi-order-excel-import-result>
  </ng-container>
</div>
